import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import ForceGraph2D from 'react-force-graph-2d';
import { Minus, Pause, Play, Plus, RotateCcw, Scan, Tag } from 'lucide-react';
import { ENTITY_TONE, IconButton, RELATION_TONE } from '../../../components/ui';
import { NetworkAnalysis } from '../graphAnalytics';
import type { ConnectionLead, NetworkLink, NetworkNode, RelationKind } from '../types';
import {
  Point,
  RadialLayout,
  Region,
  View,
  clusterRegions,
  easeInOutCubic,
  fitView,
  flightPath,
  forceCollideGrid,
  forceTarget,
  paddedHull,
  radialLayout,
} from './layout';
import { Interval, KIND_LABEL, Lens, formatSpan, isActiveAt, rankLabel } from './networkModel';
import { EntityAvatar, RolePill } from './ui';

/* ------------------------------------------------------------------ */
/* Visual constants                                                    */
/* ------------------------------------------------------------------ */

const FONT = 'Geist, system-ui, sans-serif';
const RED: [number, number, number] = [234, 42, 51];
const INK: [number, number, number] = [10, 10, 10];

const rgb = (hex: string): [number, number, number] => {
  const n = parseInt(hex.replace('#', ''), 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
};
const rgba = (c: [number, number, number], a: number) => `rgba(${c[0]},${c[1]},${c[2]},${Math.max(0, Math.min(1, a)).toFixed(3)})`;

const NODE_RGB = { stakeholder: rgb(ENTITY_TONE.stakeholder.hex), organization: rgb(ENTITY_TONE.organization.hex) };
const TYPE_RGB: Record<RelationKind, [number, number, number]> = {
  os: rgb(RELATION_TONE.os.hex),
  oo: rgb(RELATION_TONE.oo.hex),
  ss: rgb(RELATION_TONE.ss.hex),
};

const LINK_DISTANCE: Record<string, number> = { hierarchy: 58, employment: 70, relationship: 84, co_affiliation: 96 };

/* ------------------------------------------------------------------ */
/* Types                                                               */
/* ------------------------------------------------------------------ */

interface CanvasNode {
  id: string;
  data: NetworkNode;
  x?: number;
  y?: number;
  vx?: number;
  vy?: number;
  fx?: number;
  fy?: number;
  born: number;
  alpha: number;
  rDraw: number;
  userPinned?: boolean;
  lensPinned?: boolean;
}

interface CanvasLink {
  id: string;
  source: string | CanvasNode;
  target: string | CanvasNode;
  data: NetworkLink;
  alpha: number;
  curvature: number;
  active: boolean;
  __controlPoints?: number[] | null;
}

export interface NetworkCanvasHandle {
  flyTo: (id: string, zoom?: number) => void;
  fit: (ids?: string[]) => void;
  fitCluster: (community: number) => void;
  zoomBy: (factor: number) => void;
}

export interface NetworkCanvasProps {
  nodes: NetworkNode[];
  links: NetworkLink[];
  analysis: NetworkAnalysis;
  intervals: Map<string, Interval[]>;
  /** time-slider position; null when time mode is off */
  time: number | null;
  lens: Lens;
  egoId: string | null;
  selectedId: string | null;
  compareId: string | null;
  /** hover coming from panels (lists, chips) */
  hoverId: string | null;
  /** AI-referenced entities (red pulse) */
  highlightIds: string[];
  highlightCluster: number | null;
  paths: string[][];
  activePath: number;
  leads: ConnectionLead[];
  leadFocusId: string | null;
  /** lead id → accept time, animates dashed → solid */
  solidifying: Map<string, number>;
  picked: string[];
  pickMode: 'path' | 'link' | null;
  clusterNames: Map<number, string>;
  showLabels: boolean;
  onToggleLabels: () => void;
  reducedMotion: boolean;
  roleLineOf: (node: NetworkNode) => string;
  onNodeClick: (id: string) => void;
  onNodeDoubleClick: (id: string) => void;
  onLinkClick: (link: NetworkLink) => void;
  onBackgroundClick: () => void;
  onHoverChange?: (id: string | null) => void;
  /** extra left padding for overlays (e.g. a floating panel) */
  className?: string;
  compactControls?: boolean;
  /** height covered by overlays at the bottom (phone sheet) */
  insetBottom?: number;
}

/* ------------------------------------------------------------------ */
/* Drawing helpers                                                     */
/* ------------------------------------------------------------------ */

function shapePath(ctx: CanvasRenderingContext2D, type: string, x: number, y: number, r: number) {
  ctx.beginPath();
  if (type === 'organization') {
    const h = r * 0.9;
    const c = h * 0.42;
    ctx.moveTo(x - h + c, y - h);
    ctx.lineTo(x + h - c, y - h);
    ctx.quadraticCurveTo(x + h, y - h, x + h, y - h + c);
    ctx.lineTo(x + h, y + h - c);
    ctx.quadraticCurveTo(x + h, y + h, x + h - c, y + h);
    ctx.lineTo(x - h + c, y + h);
    ctx.quadraticCurveTo(x - h, y + h, x - h, y + h - c);
    ctx.lineTo(x - h, y - h + c);
    ctx.quadraticCurveTo(x - h, y - h, x - h + c, y - h);
    ctx.closePath();
  } else {
    ctx.arc(x, y, r, 0, Math.PI * 2);
  }
}

/** Smooth closed curve through hull points (quadratic midpoints) */
function smoothClosedPath(ctx: CanvasRenderingContext2D, pts: Array<[number, number]>) {
  ctx.beginPath();
  if (pts.length < 3) return;
  const mid = (a: [number, number], b: [number, number]) => [(a[0] + b[0]) / 2, (a[1] + b[1]) / 2];
  const start = mid(pts[pts.length - 1], pts[0]);
  ctx.moveTo(start[0], start[1]);
  for (let i = 0; i < pts.length; i++) {
    const p = pts[i];
    const m = mid(p, pts[(i + 1) % pts.length]);
    ctx.quadraticCurveTo(p[0], p[1], m[0], m[1]);
  }
  ctx.closePath();
}

const lerp = (a: number, b: number, t: number) => a + (b - a) * t;

/** Point on a (possibly curved) link at t, oriented from `from` */
function pointOn(link: CanvasLink, t: number, forward: boolean): Point {
  const s = link.source as CanvasNode;
  const e = link.target as CanvasNode;
  const [a, b] = forward ? [s, e] : [e, s];
  const cp = link.__controlPoints;
  if (cp && cp.length === 2) {
    const u = t;
    return {
      x: (1 - u) * (1 - u) * (a.x || 0) + 2 * (1 - u) * u * cp[0] + u * u * (b.x || 0),
      y: (1 - u) * (1 - u) * (a.y || 0) + 2 * (1 - u) * u * cp[1] + u * u * (b.y || 0),
    };
  }
  return { x: lerp(a.x || 0, b.x || 0, t), y: lerp(a.y || 0, b.y || 0, t) };
}

/** Strokes the first `t` of a link, from `forward` endpoint */
function partialLinkPath(ctx: CanvasRenderingContext2D, link: CanvasLink, t: number, forward: boolean) {
  const s = link.source as CanvasNode;
  const e = link.target as CanvasNode;
  const [a, b] = forward ? [s, e] : [e, s];
  const cp = link.__controlPoints;
  ctx.beginPath();
  ctx.moveTo(a.x || 0, a.y || 0);
  if (cp && cp.length === 2) {
    const q0x = lerp(a.x || 0, cp[0], t);
    const q0y = lerp(a.y || 0, cp[1], t);
    const q1x = lerp(cp[0], b.x || 0, t);
    const q1y = lerp(cp[1], b.y || 0, t);
    ctx.quadraticCurveTo(q0x, q0y, lerp(q0x, q1x, t), lerp(q0y, q1y, t));
  } else {
    ctx.lineTo(lerp(a.x || 0, b.x || 0, t), lerp(a.y || 0, b.y || 0, t));
  }
}

const easeOutBack = (t: number) => {
  const c1 = 1.70158;
  const c3 = c1 + 1;
  return 1 + c3 * Math.pow(t - 1, 3) + c1 * Math.pow(t - 1, 2);
};

const truncate = (s: string, n = 24) => (s.length > n ? `${s.slice(0, n - 1)}…` : s);

/* ------------------------------------------------------------------ */
/* Component                                                           */
/* ------------------------------------------------------------------ */

type Emphasis =
  | { mode: 'none' }
  | { mode: 'path'; set: Set<string> }
  | { mode: 'hover'; id: string; set: Set<string> }
  | { mode: 'cluster'; set: Set<string> }
  | { mode: 'selection'; id: string; compareId: string | null; set: Set<string> }
  | { mode: 'highlight'; set: Set<string> };

interface PathSegment {
  path: number;
  seg: number;
  segs: number;
  forward: boolean;
}

interface LabelCandidate {
  x: number;
  y: number;
  r: number;
  text: string;
  priority: number;
  alpha: number;
  strong: boolean;
}

export const NetworkCanvas = forwardRef<NetworkCanvasHandle, NetworkCanvasProps>((props, ref) => {
  const {
    nodes,
    links,
    analysis,
    lens,
    egoId,
    selectedId,
    compareId,
    hoverId,
    highlightIds,
    highlightCluster,
    paths,
    activePath,
    reducedMotion,
    showLabels,
    time,
  } = props;
  const fgRef = useRef<any>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [dims, setDims] = useState({ width: 800, height: 600 });
  const propsRef = useRef(props);
  propsRef.current = props;

  const [canvasHover, setCanvasHover] = useState<{ kind: 'node'; id: string } | { kind: 'link'; id: string } | null>(null);
  const [paused, setPaused] = useState(false);
  // Lay out the first frame before painting so the opening view is already framed
  const [warmup, setWarmup] = useState(() => Math.min(120, Math.round(24000 / Math.max(40, props.nodes.length))));
  const [animating, setAnimating] = useState(true);
  const animatingRef = useRef(true);
  const lastClick = useRef<{ id: string; at: number; x: number; y: number } | null>(null);
  const fitOnSettle = useRef(true);
  const interacted = useRef(false);

  /* ---------- sizing ---------- */
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    let frame = 0;
    const update = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        const rect = el.getBoundingClientRect();
        setDims((d) => {
          const width = Math.max(200, Math.floor(rect.width));
          const height = Math.max(200, Math.floor(rect.height));
          return d.width === width && d.height === height ? d : { width, height };
        });
      });
    };
    update();
    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', update);
      return () => window.removeEventListener('resize', update);
    }
    const observer = new ResizeObserver(update);
    observer.observe(el);
    return () => {
      observer.disconnect();
      cancelAnimationFrame(frame);
    };
  }, []);

  /* ---------- redraw control: continuous only while something animates ---------- */
  const kick = useCallback(() => {
    if (!animatingRef.current) {
      animatingRef.current = true;
      setAnimating(true);
    }
  }, []);

  /* ---------- persistent simulation objects ---------- */
  const simNodes = useRef(new Map<string, CanvasNode>());
  const simLinks = useRef(new Map<string, CanvasLink>());

  const graphData = useMemo(() => {
    const now = performance.now();
    const known = simNodes.current;
    const neighbourOf = new Map<string, string[]>();
    links.forEach((l) => {
      neighbourOf.set(l.source, [...(neighbourOf.get(l.source) || []), l.target]);
      neighbourOf.set(l.target, [...(neighbourOf.get(l.target) || []), l.source]);
    });
    const outNodes = nodes.map((n) => {
      let s = known.get(n.id);
      if (!s) {
        // New entities grow out of a neighbour that is already placed
        const anchor = (neighbourOf.get(n.id) || []).map((id) => known.get(id)).find((k) => k && k.x !== undefined);
        const spread = anchor ? 24 : 60 + Math.sqrt(nodes.length) * 30;
        s = {
          id: n.id,
          data: n,
          born: now,
          alpha: 0,
          rDraw: 0,
          x: (anchor?.x || 0) + (Math.random() - 0.5) * spread,
          y: (anchor?.y || 0) + (Math.random() - 0.5) * spread,
        };
        known.set(n.id, s);
      } else {
        s.data = n;
      }
      return s;
    });

    const pairs = new Map<string, CanvasLink[]>();
    const outLinks = links.map((l) => {
      // Fresh objects each time: the running simulation still holds the previous ones
      const prev = simLinks.current.get(l.id);
      const s: CanvasLink = {
        id: l.id,
        source: l.source,
        target: l.target,
        data: l,
        alpha: prev?.alpha ?? 0,
        curvature: 0,
        active: prev?.active ?? true,
      };
      simLinks.current.set(l.id, s);
      const key = l.source < l.target ? `${l.source}|${l.target}` : `${l.target}|${l.source}`;
      pairs.set(key, [...(pairs.get(key) || []), s]);
      return s;
    });
    // Gentle curves for person–person ties; parallel ties fan out
    pairs.forEach((group) => {
      group.forEach((s, i) => {
        const base = group.length === 1 ? (s.data.type === 'ss' ? 0.14 : 0) : (i % 2 === 0 ? 1 : -1) * (0.12 + 0.14 * Math.floor(i / 2)) + (i === 0 ? -0.12 : 0);
        const flip = s.data.source > s.data.target ? -1 : 1;
        s.curvature = base * flip;
      });
    });
    return { nodes: outNodes, links: outLinks };
  }, [nodes, links]);

  const graphDataRef = useRef(graphData);
  graphDataRef.current = graphData;

  /* ---------- derived state for painters ---------- */
  const highlightSet = useMemo(() => new Set(highlightIds), [highlightIds]);
  const effectiveHover = canvasHover?.kind === 'node' ? canvasHover.id : hoverId;

  const emphasis: Emphasis = useMemo(() => {
    const g = analysis.graph;
    const withNeighbours = (id: string) => {
      const set = new Set<string>([id]);
      const i = g.index.get(id);
      if (i !== undefined) g.nbrs[i].forEach((j) => set.add(g.ids[j]));
      return set;
    };
    if (paths.length) return { mode: 'path', set: new Set(paths.flat()) };
    if (effectiveHover) return { mode: 'hover', id: effectiveHover, set: withNeighbours(effectiveHover) };
    if (highlightCluster !== null) {
      const c = analysis.communities.find((k) => k.id === highlightCluster);
      if (c) return { mode: 'cluster', set: new Set(c.members) };
    }
    if (selectedId) {
      const set = withNeighbours(selectedId);
      if (compareId) withNeighbours(compareId).forEach((id) => set.add(id));
      return { mode: 'selection', id: selectedId, compareId, set };
    }
    if (highlightSet.size) return { mode: 'highlight', set: highlightSet };
    return { mode: 'none' };
  }, [analysis, paths, effectiveHover, highlightCluster, selectedId, compareId, highlightSet]);

  // Which link draws which path segment
  const pathSegments = useMemo(() => {
    const out = new Map<string, PathSegment[]>();
    if (!paths.length) return out;
    const byPair = new Map<string, CanvasLink>();
    graphData.links.forEach((l) => {
      const key = l.data.source < l.data.target ? `${l.data.source}|${l.data.target}` : `${l.data.target}|${l.data.source}`;
      const prev = byPair.get(key);
      if (!prev || (!prev.active && l.active)) byPair.set(key, l);
    });
    paths.forEach((p, pi) => {
      for (let k = 0; k < p.length - 1; k++) {
        const key = p[k] < p[k + 1] ? `${p[k]}|${p[k + 1]}` : `${p[k + 1]}|${p[k]}`;
        const link = byPair.get(key);
        if (!link) continue;
        const list = out.get(link.id) || [];
        list.push({ path: pi, seg: k, segs: p.length - 1, forward: link.data.source === p[k] });
        out.set(link.id, list);
      }
    });
    return out;
  }, [paths, graphData]);

  const pathStart = useRef(performance.now());
  useEffect(() => {
    pathStart.current = performance.now();
  }, [paths, activePath]);

  /* ---------- lens geometry ---------- */
  const radial: RadialLayout | null = useMemo(
    () => (lens === 'focus' && egoId && analysis.metrics.has(egoId) ? radialLayout(analysis, egoId, nodes.map((n) => n.id)) : null),
    [lens, egoId, analysis, nodes],
  );

  const regions = useMemo(() => {
    if (lens !== 'clusters') return null;
    const groups = analysis.communities.map((c) => ({ key: c.id, size: c.members.length }));
    const clustered = new Set(analysis.communities.flatMap((c) => c.members));
    const rest = nodes.filter((n) => !clustered.has(n.id)).length;
    if (rest) groups.push({ key: -1, size: Math.max(2, Math.round(rest * 0.6)) });
    return clusterRegions(groups);
  }, [lens, analysis, nodes]);

  const regionOfNode = useCallback(
    (id: string): Region | null => {
      const r = regions;
      if (!r) return null;
      const m = analysis.metrics.get(id);
      const inCluster = m && analysis.communities.some((c) => c.id === m.community);
      return r.get(inCluster ? m!.community : -1) || null;
    },
    [regions, analysis],
  );

  const frame = useRef({
    now: 0,
    last: 0,
    dt: 16,
    k: 1,
    scale: 1,
    active: false,
    labels: [] as LabelCandidate[],
    hullAlpha: 0,
    ringAlpha: 0,
    timeOn: false,
    activeNodes: new Set<string>(),
    linkedNodes: new Set<string>(),
  });
  const derived = useRef({ emphasis, pathSegments, radial, highlightSet, regions });
  derived.current = { emphasis, pathSegments, radial, highlightSet, regions };

  /* ---------- camera ---------- */
  const flight = useRef<number>(0);
  const flyToView = useCallback(
    (view: View | null) => {
      const fg = fgRef.current;
      if (!fg || !view) return;
      cancelAnimationFrame(flight.current);
      if (propsRef.current.reducedMotion) {
        fg.zoom(view.k);
        fg.centerAt(view.x, view.y);
        return;
      }
      const c = fg.centerAt();
      const from: View = { x: c.x, y: c.y, k: fg.zoom() };
      const { at, duration } = flightPath(from, view, dims.width);
      const start = performance.now();
      const step = () => {
        const t = Math.min(1, (performance.now() - start) / duration);
        const v = at(easeInOutCubic(t));
        fg.zoom(v.k);
        fg.centerAt(v.x, v.y);
        if (t < 1) flight.current = requestAnimationFrame(step);
      };
      flight.current = requestAnimationFrame(step);
    },
    [dims.width],
  );
  useEffect(() => () => cancelAnimationFrame(flight.current), []);

  const pointsOf = useCallback(
    (ids?: string[]) => {
      const inGraph = new Set(graphData.nodes);
      return (ids ? ids.map((id) => simNodes.current.get(id)) : graphData.nodes)
        .filter((n): n is CanvasNode => !!n && n.x !== undefined && inGraph.has(n))
        .map((n) => ({ x: n.x!, y: n.y!, r: (n.rDraw || 6) + 18 }));
    },
    [graphData],
  );

  // Fits points into the part of the canvas not covered by overlays (e.g. the phone sheet)
  const frameFit = useCallback(
    (points: Array<{ x: number; y: number; r?: number }>, padding = 48, maxZoom = 2.4): View | null => {
      const inset = propsRef.current.insetBottom || 0;
      const view = fitView(points, dims.width, Math.max(160, dims.height - inset), padding, maxZoom);
      return view ? { ...view, y: view.y + inset / 2 / view.k } : null;
    },
    [dims],
  );
  const fitAllRef = useRef(() => {});
  fitAllRef.current = () => flyToView(frameFit(pointsOf()));

  useImperativeHandle(
    ref,
    () => ({
      flyTo: (id, zoom) => {
        const n = simNodes.current.get(id);
        const fg = fgRef.current;
        if (!n || n.x === undefined || !fg) return;
        interacted.current = true;
        fitOnSettle.current = false;
        const k = Math.max(fg.zoom(), zoom ?? 1.7);
        // centre the entity in the part of the canvas that is not covered (phone sheet)
        flyToView({ x: n.x, y: n.y! + (propsRef.current.insetBottom || 0) / 2 / k, k });
      },
      fit: (ids) => {
        interacted.current = true;
        fitOnSettle.current = false;
        const pts = pointsOf(ids && ids.length ? ids : undefined);
        flyToView(frameFit(pts, 48, ids && ids.length === 1 ? 2.6 : 2.4));
      },
      fitCluster: (community) => {
        const c = analysis.communities.find((k) => k.id === community);
        if (!c) return;
        interacted.current = true;
        fitOnSettle.current = false;
        const region = regions?.get(community);
        if (region && lens === 'clusters') flyToView(frameFit([{ ...region, r: region.r + 30 }], 40, 2.6));
        else flyToView(frameFit(pointsOf(c.members), 48, 2.6));
      },
      zoomBy: (factor) => {
        const fg = fgRef.current;
        if (!fg) return;
        interacted.current = true;
        fg.zoom(fg.zoom() * factor, propsRef.current.reducedMotion ? 0 : 260);
      },
    }),
    [flyToView, pointsOf, frameFit, analysis, regions, lens],
  );

  /* ---------- forces per lens ---------- */
  const defaultLinkStrength = useRef<any>(null);
  const derivedForces = useRef({ regionOfNode, analysis });
  derivedForces.current = { regionOfNode, analysis };

  useEffect(() => {
    const fg = fgRef.current;
    if (!fg) return;
    const charge = fg.d3Force('charge');
    const linkForce = fg.d3Force('link');
    if (linkForce && !defaultLinkStrength.current) defaultLinkStrength.current = linkForce.strength();
    const count = graphData.nodes.length;
    linkForce?.distance((l: CanvasLink) => LINK_DISTANCE[l.data.kind] || 80);
    fg.d3Force('y', null);
    if (lens === 'clusters') {
      charge?.strength(-50).distanceMax(240);
      linkForce?.strength((l: CanvasLink) => {
        const m = derivedForces.current.analysis.metrics;
        const a = m.get(l.data.source)?.community;
        const b = m.get(l.data.target)?.community;
        return a !== undefined && a === b ? 0.4 : 0.003;
      });
      fg.d3Force(
        'x',
        forceTarget<CanvasNode>(
          (n) => derivedForces.current.regionOfNode(n.id),
          () => 0.16,
        ),
      );
      fg.d3Force('collide', forceCollideGrid<CanvasNode>((n) => (n.rDraw || 6) + 5, 0.8));
    } else {
      charge?.strength(count > 400 ? -70 : count > 120 ? -120 : -190).distanceMax(560);
      if (defaultLinkStrength.current) linkForce?.strength(defaultLinkStrength.current);
      fg.d3Force(
        'x',
        forceTarget<CanvasNode>(
          () => ({ x: 0, y: 0 }),
          () => 0.045,
        ),
      );
      fg.d3Force('collide', forceCollideGrid<CanvasNode>((n) => (n.rDraw || 6) + 9, 0.7));
    }
    if (lens !== 'focus') {
      graphData.nodes.forEach((n) => {
        if (n.lensPinned && !n.userPinned) {
          n.fx = undefined;
          n.fy = undefined;
        }
        n.lensPinned = false;
      });
    }
    if (!paused) fg.d3ReheatSimulation();
    kick();
  }, [lens, graphData, regions, paused, kick]);

  // Clusters/Map lens: frame the new arrangement once it has moved
  const prevLens = useRef(lens);
  useEffect(() => {
    if (prevLens.current === lens) return;
    prevLens.current = lens;
    if (lens === 'clusters' && regions) {
      const pts = Array.from(regions.values()).map((r) => ({ x: r.x, y: r.y, r: r.r + 20 }));
      flyToView(frameFit(pts, 56, 2));
    } else if (lens === 'map') {
      fitOnSettle.current = true;
      window.setTimeout(() => {
        if (fitOnSettle.current) flyToView(frameFit(pointsOf(), 48, 2.4));
      }, 900);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lens]);

  /* ---------- Focus lens: tween nodes onto rings ---------- */
  const focusTween = useRef(0);
  const lastEgo = useRef<string | null>(null);
  useEffect(() => {
    if (!radial) return;
    cancelAnimationFrame(focusTween.current);
    const from = new Map<string, Point>();
    graphData.nodes.forEach((n) => from.set(n.id, { x: n.x || 0, y: n.y || 0 }));
    const duration = reducedMotion ? 0 : 760;
    const start = performance.now();
    kick();
    const step = () => {
      const t = duration ? Math.min(1, (performance.now() - start) / duration) : 1;
      const e = easeInOutCubic(t);
      graphData.nodes.forEach((n) => {
        const target = radial.positions.get(n.id);
        const origin = from.get(n.id);
        if (!target || !origin) return;
        n.fx = lerp(origin.x, target.x, e);
        n.fy = lerp(origin.y, target.y, e);
        n.x = n.fx;
        n.y = n.fy;
        n.lensPinned = true;
      });
      if (t < 1) {
        kick();
        focusTween.current = requestAnimationFrame(step);
      }
    };
    focusTween.current = requestAnimationFrame(step);
    if (lastEgo.current !== egoId || prevFocusLens.current !== 'focus') {
      lastEgo.current = egoId;
      const inner = radial.rings.filter((r) => r.hop <= 2);
      const radius = (inner.length ? inner[inner.length - 1].radius : radial.rings[0]?.radius || 160) + 40;
      flyToView(frameFit([{ x: 0, y: 0, r: radius }], 24, 2.4));
    }
    return () => cancelAnimationFrame(focusTween.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [radial]);
  const prevFocusLens = useRef(lens);
  useEffect(() => {
    prevFocusLens.current = lens;
  });

  /* ---------- kick on visual prop changes ---------- */
  useEffect(() => {
    kick();
  }, [kick, emphasis, pathSegments, highlightSet, time, showLabels, props.leads, props.leadFocusId, props.solidifying, props.picked, lens, analysis]);

  /* ---------- painters ---------- */
  const onRenderFramePre = useCallback((ctx: CanvasRenderingContext2D, scale: number) => {
    const P = propsRef.current;
    const D = derived.current;
    const F = frame.current;
    const now = performance.now();
    F.dt = Math.min(100, F.last ? now - F.last : 16);
    F.last = now;
    F.now = now;
    F.k = P.reducedMotion ? 1 : 1 - Math.exp(-F.dt / 85);
    F.scale = scale;
    F.active = false;
    F.labels.length = 0;

    // Time mode: which ties exist now
    F.timeOn = P.time !== null;
    F.activeNodes.clear();
    F.linkedNodes.clear();
    const fgLinks: CanvasLink[] = fgRef.current ? (graphDataRef.current.links as CanvasLink[]) : [];
    fgLinks.forEach((l) => {
      l.active = P.time === null || isActiveAt(P.intervals.get(l.id) || [[-Infinity, Infinity]], P.time);
      F.linkedNodes.add(l.data.source);
      F.linkedNodes.add(l.data.target);
      if (l.active) {
        F.activeNodes.add(l.data.source);
        F.activeNodes.add(l.data.target);
      }
    });

    // Subtle dot grid
    if (scale > 0.35 && scale < 8 && fgRef.current) {
      const tl = fgRef.current.screen2GraphCoords(0, 0);
      const ratio = window.devicePixelRatio || 1;
      const br = fgRef.current.screen2GraphCoords(ctx.canvas.width / ratio, ctx.canvas.height / ratio);
      const grid = 28;
      ctx.save();
      ctx.fillStyle = 'rgba(10,10,10,0.055)';
      const r = 0.7 / scale;
      for (let gx = Math.floor(tl.x / grid) * grid; gx <= br.x; gx += grid) {
        for (let gy = Math.floor(tl.y / grid) * grid; gy <= br.y; gy += grid) {
          ctx.fillRect(gx - r, gy - r, r * 2, r * 2);
        }
      }
      ctx.restore();
    }

    // Cluster hulls (neutral)
    const hullTarget = P.lens === 'clusters' ? 1 : 0;
    F.hullAlpha += (hullTarget - F.hullAlpha) * F.k;
    if (Math.abs(hullTarget - F.hullAlpha) > 0.01) F.active = true;
    const showHull = (communityId: number) => F.hullAlpha > 0.02 || P.highlightCluster === communityId;
    ctx.save();
    P.analysis.communities.forEach((c) => {
      if (!showHull(c.id)) return;
      const circles = c.members
        .map((id) => simNodes.current.get(id))
        .filter((n): n is CanvasNode => !!n && n.x !== undefined && n.alpha > 0.05)
        .map((n) => ({ x: n.x!, y: n.y!, r: n.rDraw || 5 }));
      if (circles.length < 2) return;
      const hull = paddedHull(circles, 14, 10);
      const strong = P.highlightCluster === c.id;
      const a = strong ? 1 : F.hullAlpha;
      smoothClosedPath(ctx, hull);
      ctx.fillStyle = rgba(INK, (strong ? 0.05 : 0.026) * a);
      ctx.fill();
      ctx.lineWidth = 1 / scale;
      ctx.strokeStyle = rgba(INK, (strong ? 0.3 : 0.13) * a);
      ctx.stroke();
      // Label above the hull
      let top = hull[0];
      hull.forEach((p) => {
        if (p[1] < top[1]) top = p;
      });
      const name = P.clusterNames.get(c.id);
      if (name) {
        const size = 11 / scale;
        ctx.font = `500 ${size}px ${FONT}`;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'bottom';
        const text = `${truncate(name, 28)} · ${c.members.length}`;
        ctx.lineJoin = 'round';
        ctx.lineWidth = 3 / scale;
        ctx.strokeStyle = rgba([255, 255, 255], 0.95 * a);
        ctx.strokeText(text, top[0], top[1] - 5 / scale);
        ctx.fillStyle = rgba([115, 115, 115], a);
        ctx.fillText(text, top[0], top[1] - 5 / scale);
      }
    });
    ctx.restore();

    // Focus rings
    const ringTarget = P.lens === 'focus' && D.radial ? 1 : 0;
    F.ringAlpha += (ringTarget - F.ringAlpha) * F.k;
    if (Math.abs(ringTarget - F.ringAlpha) > 0.01) F.active = true;
    if (F.ringAlpha > 0.02 && D.radial) {
      ctx.save();
      D.radial.rings.forEach((ring) => {
        ctx.beginPath();
        ctx.arc(0, 0, ring.radius, 0, Math.PI * 2);
        ctx.lineWidth = 1 / scale;
        ctx.strokeStyle = rgba(INK, (ring.hop === 3 ? 0.05 : 0.09) * F.ringAlpha);
        ctx.setLineDash(ring.hop === 3 ? [3 / scale, 4 / scale] : []);
        ctx.stroke();
        const size = 10.5 / scale;
        ctx.setLineDash([]);
        ctx.font = `400 ${size}px ${FONT}`;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        const label = `${ring.hop === 3 ? 'Further' : ring.hop === 1 ? '1 hop' : '2 hops'} · ${ring.count}`;
        const w = ctx.measureText(label).width + 12 / scale;
        ctx.fillStyle = rgba([255, 255, 255], 0.92 * F.ringAlpha);
        ctx.fillRect(-w / 2, -ring.radius - size, w, size * 2);
        ctx.fillStyle = rgba([163, 163, 163], F.ringAlpha);
        ctx.fillText(label, 0, -ring.radius);
      });
      ctx.restore();
    }

    // AI hypothesis edges: dashed in their kind colour, marching slowly
    if (P.leads.length) {
      ctx.save();
      P.leads.forEach((lead) => {
        const s = simNodes.current.get(lead.subjectId);
        const t = lead.targetId ? simNodes.current.get(lead.targetId) : undefined;
        if (!s || !t || s.x === undefined || t.x === undefined || s.alpha < 0.05 || t.alpha < 0.05) return;
        const focus = P.leadFocusId === lead.id;
        const solidAt = P.solidifying.get(lead.id);
        const progress = solidAt ? Math.min(1, (now - solidAt) / 900) : 0;
        if (solidAt && progress < 1) F.active = true;
        const color = focus ? RED : TYPE_RGB[lead.relationKind] || TYPE_RGB.os;
        ctx.beginPath();
        ctx.moveTo(s.x, s.y!);
        ctx.lineTo(t.x, t.y!);
        ctx.lineWidth = (focus ? 2.4 : 1.6) / scale;
        // Hypotheses fade with their endpoints so they never shout over a selection
        ctx.strokeStyle = rgba(color, focus ? 0.95 : 0.8 * Math.min(s.alpha, t.alpha));
        ctx.setLineDash([5 / scale, (4 * (1 - progress)) / scale]);
        ctx.lineDashOffset = P.reducedMotion ? 0 : -((now / 55) % 900) / scale;
        ctx.stroke();
      });
      ctx.restore();
      if (!P.reducedMotion) F.active = true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const paintLink = useCallback((link: CanvasLink, ctx: CanvasRenderingContext2D, scale: number) => {
    const s = link.source as CanvasNode;
    const t = link.target as CanvasNode;
    if (!s || !t || s.x === undefined || t.x === undefined) return;
    const P = propsRef.current;
    const D = derived.current;
    const F = frame.current;
    const E = D.emphasis;
    const d = link.data;
    const sid = s.id;
    const tid = t.id;

    let target = d.isCurrent === false ? 0.16 : 0.34;
    let strong = false;
    if (F.timeOn && !link.active) target = 0;
    else if (E.mode === 'path') target = E.set.has(sid) && E.set.has(tid) ? 0.2 : 0.045;
    else if (E.mode === 'hover') {
      strong = sid === E.id || tid === E.id;
      target = strong ? 0.9 : 0.05;
    } else if (E.mode === 'cluster') {
      strong = E.set.has(sid) && E.set.has(tid);
      target = strong ? 0.6 : 0.05;
    } else if (E.mode === 'selection') {
      strong = sid === E.id || tid === E.id || (!!E.compareId && (sid === E.compareId || tid === E.compareId));
      target = strong ? 0.85 : 0.09;
      // the Focus lens is about the whole neighbourhood: keep ties within two hops readable
      if (!strong && P.lens === 'focus' && (D.radial?.ringOf.get(sid) ?? 3) <= 2 && (D.radial?.ringOf.get(tid) ?? 3) <= 2) target = 0.34;
    } else if (E.mode === 'highlight') {
      strong = E.set.has(sid) && E.set.has(tid);
      target = strong ? 0.6 : 0.16;
    }
    if (P.lens === 'focus' && D.radial && (D.radial.ringOf.get(sid) === 3 || D.radial.ringOf.get(tid) === 3)) {
      target = Math.min(target, 0.05);
    }
    link.alpha += (target - link.alpha) * F.k;
    if (Math.abs(target - link.alpha) > 0.004) F.active = true;

    const segments = D.pathSegments.get(link.id);
    if (link.alpha > 0.01) {
      const width = ((0.75 + d.weight * 0.22) * (strong ? 1.5 : 1)) / scale;
      ctx.beginPath();
      ctx.moveTo(s.x, s.y!);
      const cp = link.__controlPoints;
      if (cp && cp.length === 2) ctx.quadraticCurveTo(cp[0], cp[1], t.x, t.y!);
      else ctx.lineTo(t.x, t.y!);
      ctx.lineWidth = width;
      ctx.strokeStyle = rgba(TYPE_RGB[d.type] || TYPE_RGB.os, link.alpha);
      ctx.setLineDash(d.isCurrent === false ? [2 / scale, 2.5 / scale] : []);
      ctx.stroke();
      ctx.setLineDash([]);
    }

    // Paths: draw themselves segment by segment, then particles travel along
    if (segments) {
      const elapsed = F.now - pathStart.current;
      segments.forEach((seg) => {
        const active = seg.path === P.activePath;
        const perSeg = P.reducedMotion ? 0 : active ? 260 : 360;
        const delay = active ? 0 : 200;
        const progress = perSeg ? Math.max(0, Math.min(1, (elapsed - delay - seg.seg * perSeg) / perSeg)) : 1;
        if (progress < 1) F.active = true;
        if (progress <= 0) return;
        partialLinkPath(ctx, link, easeInOutCubic(progress), seg.forward);
        ctx.lineCap = 'round';
        ctx.lineWidth = (active ? 2.6 : 1.6) / scale;
        ctx.strokeStyle = rgba(RED, active ? 0.92 : 0.28);
        ctx.stroke();
        ctx.lineCap = 'butt';
        if (active && !P.reducedMotion && elapsed > seg.segs * perSeg) {
          F.active = true;
          const lap = seg.segs * 700;
          [0, 0.5].forEach((offset) => {
            const phase = (((elapsed / lap + offset) % 1) + 1) % 1;
            const along = phase * seg.segs;
            if (Math.floor(along) !== seg.seg) return;
            const p = pointOn(link, along - seg.seg, seg.forward);
            ctx.beginPath();
            ctx.arc(p.x, p.y, 2.6 / scale, 0, Math.PI * 2);
            ctx.fillStyle = rgba(RED, 0.95);
            ctx.fill();
          });
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const paintNode = useCallback((node: CanvasNode, ctx: CanvasRenderingContext2D, scale: number) => {
    if (node.x === undefined || node.y === undefined) return;
    const P = propsRef.current;
    const D = derived.current;
    const F = frame.current;
    const E = D.emphasis;
    const id = node.id;
    const m = P.analysis.metrics.get(id);
    const x = node.x;
    const y = node.y;

    // Target opacity
    let target = 1;
    if (F.timeOn && F.linkedNodes.has(id) && !F.activeNodes.has(id)) target = 0.1;
    if (P.lens === 'focus' && D.radial && D.radial.ringOf.get(id) === 3) target = Math.min(target, 0.22);
    const inFocus = E.mode !== 'none' && E.set.has(id);
    if (E.mode === 'path' && !inFocus) target = Math.min(target, 0.14);
    else if (E.mode === 'hover' && !inFocus) target = Math.min(target, 0.14);
    else if (E.mode === 'cluster' && !inFocus) target = Math.min(target, 0.16);
    else if (E.mode === 'selection' && !inFocus && !(P.lens === 'focus' && (D.radial?.ringOf.get(id) ?? 3) <= 2)) target = Math.min(target, 0.34);
    else if (E.mode === 'highlight' && !inFocus) target = Math.min(target, 0.42);
    const picked = P.picked.includes(id);
    if (picked || id === P.selectedId) target = 1;
    node.alpha += (target - node.alpha) * F.k;
    if (Math.abs(target - node.alpha) > 0.004) F.active = true;

    const pct = m ? m.pct.influence : 0;
    const rTarget = m && m.degree > 0 ? 4.5 + 10 * Math.pow(pct / 100, 1.7) : 4;
    node.rDraw = node.rDraw ? node.rDraw + (rTarget - node.rDraw) * F.k : rTarget;
    if (Math.abs(rTarget - node.rDraw) > 0.02) F.active = true;
    const age = (F.now - node.born) / 520;
    const grow = P.reducedMotion || age >= 1 ? 1 : Math.max(0.01, easeOutBack(Math.max(0, age)));
    if (age < 1) F.active = true;
    const r = node.rDraw * grow;
    if (node.alpha < 0.012) return;

    const type = node.data.type;
    const color = NODE_RGB[type] || NODE_RGB.stakeholder;
    const selected = id === P.selectedId;
    const compared = id === P.compareId;
    const hovered = id === (E.mode === 'hover' ? E.id : null);
    const onActivePath = !!P.paths[P.activePath]?.includes(id);
    const endpoint = P.paths.length > 0 && (P.paths[0][0] === id || P.paths[0][P.paths[0].length - 1] === id);
    const highlighted = D.highlightSet.has(id);

    ctx.save();
    ctx.globalAlpha = node.alpha;

    // AI focus: red pulse
    if (highlighted) {
      // Many references: a quiet halo instead of a field of pulses
      if (P.reducedMotion || D.highlightSet.size > 10) {
        shapePath(ctx, type, x, y, r + 5 / scale);
        ctx.fillStyle = rgba(RED, 0.16);
        ctx.fill();
      } else {
        F.active = true;
        [0, 0.5].forEach((offset) => {
          const phase = ((F.now / 1700 + offset) % 1 + 1) % 1;
          shapePath(ctx, type, x, y, r + (3 + phase * 13) / scale);
          ctx.lineWidth = 1.5 / scale;
          ctx.strokeStyle = rgba(RED, 0.55 * (1 - phase));
          ctx.stroke();
        });
      }
    }

    // Halo
    if (selected || picked || endpoint) {
      shapePath(ctx, type, x, y, r + 6 / scale);
      ctx.fillStyle = rgba(RED, 0.13);
      ctx.fill();
    } else if (hovered) {
      shapePath(ctx, type, x, y, r + 5 / scale);
      ctx.fillStyle = rgba(color, 0.2);
      ctx.fill();
    }

    // Body
    shapePath(ctx, type, x, y, r);
    ctx.fillStyle = rgba(color, 1);
    ctx.fill();
    ctx.lineWidth = 1 / scale;
    ctx.strokeStyle = 'rgba(255,255,255,0.95)';
    ctx.stroke();

    if (selected || picked || onActivePath || endpoint) {
      shapePath(ctx, type, x, y, r + 2.2 / scale);
      ctx.lineWidth = (selected || picked || endpoint ? 1.8 : 1.3) / scale;
      ctx.strokeStyle = rgba(RED, 1);
      ctx.stroke();
    }
    if (compared) {
      shapePath(ctx, type, x, y, r + 2.4 / scale);
      ctx.setLineDash([2.5 / scale, 2 / scale]);
      ctx.lineWidth = 1.4 / scale;
      ctx.strokeStyle = rgba(INK, 0.85);
      ctx.stroke();
      ctx.setLineDash([]);
    }
    if (node.userPinned) {
      ctx.beginPath();
      ctx.arc(x + r * 0.72, y - r * 0.72, 1.6 / scale, 0, Math.PI * 2);
      ctx.fillStyle = rgba(INK, 0.6);
      ctx.fill();
    }
    ctx.restore();

    // Label candidates, drawn after all nodes with collision avoidance
    if (P.showLabels && node.alpha > 0.3) {
      const strongLabel = selected || compared || picked || hovered || endpoint || highlighted;
      const ring = D.radial?.ringOf.get(id);
      let priority = 10 + pct / 10;
      if (selected) priority = 100;
      else if (hovered) priority = 98;
      else if (picked || endpoint) priority = 95;
      else if (compared) priority = 92;
      else if (highlighted) priority = 88;
      else if (onActivePath) priority = 80;
      else if (inFocus) priority = 60 + pct / 10;
      else if (P.lens === 'focus' && ring !== undefined && ring <= 1) priority = 55 + pct / 10;
      const eligible =
        strongLabel ||
        onActivePath ||
        scale >= 1.4 ||
        (inFocus && D.emphasis.mode !== 'highlight') ||
        (P.lens === 'focus' && ring !== undefined && ring <= 1) ||
        pct >= 92;
      if (eligible) {
        F.labels.push({ x, y, r, text: node.data.label, priority, alpha: node.alpha, strong: strongLabel || onActivePath });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onRenderFramePost = useCallback(
    (ctx: CanvasRenderingContext2D, scale: number) => {
      const P = propsRef.current;
      const F = frame.current;
      // Labels, most important first, skipping overlaps
      const size = 11 / scale;
      const boxes: Array<[number, number, number, number]> = [];
      ctx.save();
      ctx.textAlign = 'center';
      ctx.textBaseline = 'top';
      ctx.lineJoin = 'round';
      F.labels
        .sort((a, b) => b.priority - a.priority)
        .slice(0, 400)
        .forEach((l) => {
          const text = truncate(l.text);
          ctx.font = `${l.strong ? 500 : 400} ${size}px ${FONT}`;
          const w = ctx.measureText(text).width;
          const bx = l.x - w / 2 - 2 / scale;
          const by = l.y + l.r + 3 / scale;
          const bw = w + 4 / scale;
          const bh = size + 3 / scale;
          if (!l.strong && boxes.some(([x, y, ww, hh]) => bx < x + ww && bx + bw > x && by < y + hh && by + bh > y)) return;
          boxes.push([bx, by, bw, bh]);
          ctx.globalAlpha = Math.min(1, l.alpha * 1.1);
          ctx.lineWidth = 3 / scale;
          ctx.strokeStyle = 'rgba(255,255,255,0.94)';
          ctx.strokeText(text, l.x, by + 1 / scale);
          ctx.fillStyle = l.strong ? '#0a0a0a' : '#525252';
          ctx.fillText(text, l.x, by + 1 / scale);
        });
      ctx.restore();

      const continuous =
        F.active ||
        (P.paths.length > 0 && !P.reducedMotion) ||
        (P.highlightIds.length > 0 && P.highlightIds.length <= 10 && !P.reducedMotion) ||
        (P.leads.length > 0 && !P.reducedMotion);
      if (!continuous && animatingRef.current) {
        animatingRef.current = false;
        setAnimating(false);
      }
    },
    [],
  );

  /* ---------- interaction ---------- */
  const handleNodeClick = useCallback((node: CanvasNode, event?: MouseEvent) => {
    const now = Date.now();
    const last = lastClick.current;
    const x = event?.clientX ?? 0;
    const y = event?.clientY ?? 0;
    interacted.current = true;
    // A double-click is two quick clicks on the same entity at the same spot
    if (last && last.id === node.id && now - last.at < 380 && Math.hypot(last.x - x, last.y - y) < 8) {
      lastClick.current = null;
      propsRef.current.onNodeDoubleClick(node.id);
      return;
    }
    lastClick.current = { id: node.id, at: now, x, y };
    propsRef.current.onNodeClick(node.id);
  }, []);

  const handleNodeHover = useCallback(
    (node: CanvasNode | null) => {
      setCanvasHover(node ? { kind: 'node', id: node.id } : null);
      propsRef.current.onHoverChange?.(node ? node.id : null);
      kick();
    },
    [kick],
  );

  const handleLinkHover = useCallback((link: CanvasLink | null) => {
    setCanvasHover((h) => (link ? { kind: 'link', id: link.id } : h?.kind === 'link' ? null : h));
  }, []);

  const handleLinkClick = useCallback((link: CanvasLink) => {
    if (link.data.kind === 'relationship' && link.data.relationshipId) propsRef.current.onLinkClick(link.data);
  }, []);

  const handleDragEnd = useCallback((node: CanvasNode) => {
    node.fx = node.x;
    node.fy = node.y;
    node.userPinned = true;
    interacted.current = true;
  }, []);

  const handleEngineStop = useCallback(() => {
    if (fitOnSettle.current && propsRef.current.lens !== 'focus') {
      fitOnSettle.current = false;
      fitAllRef.current();
    }
  }, []);

  // Frame the network early (the layout is warmed up) instead of waiting for it to settle
  useEffect(() => {
    const timer = window.setTimeout(() => {
      if (!interacted.current && fgRef.current && graphDataRef.current.nodes.length) fitAllRef.current();
      setWarmup(0);
    }, 450);
    return () => window.clearTimeout(timer);
  }, []);

  const resetLayout = () => {
    graphData.nodes.forEach((n) => {
      n.userPinned = false;
      if (!n.lensPinned) {
        n.fx = undefined;
        n.fy = undefined;
      }
    });
    fitOnSettle.current = true;
    setPaused(false);
    fgRef.current?.d3ReheatSimulation();
    kick();
  };

  const togglePaused = () => {
    setPaused((p) => {
      if (p) window.setTimeout(() => fgRef.current?.d3ReheatSimulation(), 0);
      return !p;
    });
  };

  /* ---------- hover card ---------- */
  const hoverCard = (() => {
    if (!canvasHover || !fgRef.current) return null;
    if (canvasHover.kind === 'node') {
      const n = simNodes.current.get(canvasHover.id);
      if (!n || n.x === undefined) return null;
      const p = fgRef.current.graph2ScreenCoords(n.x, n.y);
      const m = analysis.metrics.get(n.id);
      return { x: p.x, y: p.y, r: (n.rDraw || 6) * fgRef.current.zoom(), node: n.data, metrics: m };
    }
    const l = simLinks.current.get(canvasHover.id);
    if (!l || typeof l.source !== 'object' || typeof l.target !== 'object') return null;
    const mid = pointOn(l, 0.5, true);
    const p = fgRef.current.graph2ScreenCoords(mid.x, mid.y);
    return { x: p.x, y: p.y, r: 4, link: l.data, a: l.source.data, b: (l.target as CanvasNode).data };
  })();

  const cardLeft = hoverCard ? Math.min(Math.max(8, hoverCard.x + 14), dims.width - 250) : 0;
  const cardTop = hoverCard ? Math.min(Math.max(8, hoverCard.y + hoverCard.r + 8), dims.height - 110) : 0;

  return (
    <div className={`absolute inset-0 overflow-hidden bg-white ${props.className || ''}`}>
      <div ref={containerRef} className="absolute inset-0" onMouseLeave={() => canvasHover && handleNodeHover(null)}>
        <ForceGraph2D
          ref={fgRef}
          graphData={graphData as any}
          width={dims.width}
          height={dims.height}
          backgroundColor="#ffffff"
          autoPauseRedraw={!animating}
          nodeLabel={() => ''}
          linkLabel={() => ''}
          nodeCanvasObjectMode={() => 'replace'}
          nodeCanvasObject={paintNode as any}
          nodePointerAreaPaint={(node: any, color: string, ctx: CanvasRenderingContext2D) => {
            if ((node.alpha ?? 1) < 0.08 || node.x === undefined) return;
            ctx.fillStyle = color;
            ctx.beginPath();
            ctx.arc(node.x, node.y, (node.rDraw || 5) + 3, 0, Math.PI * 2);
            ctx.fill();
          }}
          linkCanvasObjectMode={() => 'replace'}
          linkCanvasObject={paintLink as any}
          linkCurvature={(l: any) => l.curvature || 0}
          linkWidth={(l: any) => (l.data?.kind === 'relationship' ? 3 : 1)}
          linkPointerAreaPaint={(l: any, color: string, ctx: CanvasRenderingContext2D, scale: number) => {
            if ((l.alpha ?? 0) < 0.06 || typeof l.source !== 'object' || l.source.x === undefined) return;
            ctx.beginPath();
            ctx.moveTo(l.source.x, l.source.y);
            const cp = l.__controlPoints;
            if (cp && cp.length === 2) ctx.quadraticCurveTo(cp[0], cp[1], l.target.x, l.target.y);
            else ctx.lineTo(l.target.x, l.target.y);
            ctx.strokeStyle = color;
            ctx.lineWidth = (l.data?.kind === 'relationship' ? 7 : 4) / scale;
            ctx.stroke();
          }}
          onRenderFramePre={onRenderFramePre}
          onRenderFramePost={onRenderFramePost}
          onNodeClick={handleNodeClick as any}
          onNodeHover={handleNodeHover as any}
          onNodeDragEnd={handleDragEnd as any}
          onLinkHover={handleLinkHover as any}
          onLinkClick={handleLinkClick as any}
          onBackgroundClick={() => propsRef.current.onBackgroundClick()}
          onZoom={() => {
            // react-kapsule applies props during render (a resize fires zoom): never set state synchronously here
            if (canvasHover) window.setTimeout(() => setCanvasHover(null), 0);
          }}
          onEngineStop={handleEngineStop}
          showPointerCursor={(obj: any) => !!obj && (!!obj.data?.type && (!obj.data?.kind || obj.data.kind === 'relationship'))}
          cooldownTicks={paused ? 0 : Infinity}
          cooldownTime={7000}
          warmupTicks={warmup}
          d3AlphaMin={0.004}
          d3VelocityDecay={0.32}
          minZoom={0.08}
          maxZoom={8}
        />
      </div>

      {hoverCard && (
        <div
          className="pointer-events-none absolute z-10 w-[240px] animate-fade-up rounded-xl bg-white px-3 py-2.5 font-ui shadow-[0_10px_30px_-12px_rgba(0,0,0,0.25)] ring-1 ring-black/[0.06] motion-reduce:animate-none"
          style={{ left: cardLeft, top: cardTop }}
        >
          {'node' in hoverCard && hoverCard.node ? (
            <>
              <div className="flex items-center gap-2">
                <EntityAvatar node={hoverCard.node} size="xs" />
                <div className="min-w-0">
                  <p className="truncate text-[13px] leading-tight text-ink">{hoverCard.node.label}</p>
                  <p className="truncate text-[11.5px] text-neutral-500">{props.roleLineOf(hoverCard.node)}</p>
                </div>
              </div>
              {hoverCard.metrics && (
                <div className="mt-2 flex flex-wrap items-center gap-1.5 text-[11.5px] text-neutral-500">
                  <RolePill role={hoverCard.metrics.role} />
                  {hoverCard.metrics.degree > 0 && (
                    <span className="tabular-nums">
                      {rankLabel(hoverCard.metrics.pct.influence)} influence · {hoverCard.metrics.degree}{' '}
                      {hoverCard.metrics.degree === 1 ? 'tie' : 'ties'}
                    </span>
                  )}
                </div>
              )}
            </>
          ) : 'link' in hoverCard && hoverCard.link ? (
            <>
              <p className="flex items-center gap-1.5 text-[11.5px] text-neutral-500">
                <span className="h-0.5 w-3 rounded-full" style={{ backgroundColor: RELATION_TONE[hoverCard.link.type].hex }} />
                {KIND_LABEL[hoverCard.link.kind]}
                {formatSpan(hoverCard.link.startDate, hoverCard.link.endDate, hoverCard.link.isCurrent) && (
                  <span className="tabular-nums">· {formatSpan(hoverCard.link.startDate, hoverCard.link.endDate, hoverCard.link.isCurrent)}</span>
                )}
              </p>
              <p className="mt-1 text-[13px] leading-snug text-ink">
                {hoverCard.a.label} <span className="text-neutral-400">—</span> {hoverCard.b.label}
              </p>
              {hoverCard.link.label && <p className="mt-0.5 truncate text-[12px] text-neutral-500">{hoverCard.link.label}</p>}
              {hoverCard.link.kind === 'relationship' && <p className="mt-1 text-[11px] text-neutral-400">Click to edit</p>}
            </>
          ) : null}
        </div>
      )}

      {/* Canvas controls */}
      <div
        role="toolbar"
        aria-label="Canvas controls"
        style={{ bottom: (props.insetBottom || 0) + 12 }}
        className="absolute right-3 z-10 flex flex-col items-center gap-0.5 rounded-full bg-white p-1 shadow-[0_8px_28px_-12px_rgba(0,0,0,0.22)] ring-1 ring-black/[0.06]"
      >
        <IconButton label="Zoom in" size="sm" onClick={() => fgRef.current?.zoom(fgRef.current.zoom() * 1.4, reducedMotion ? 0 : 260)}>
          <Plus className="h-4 w-4" strokeWidth={1.75} />
        </IconButton>
        <IconButton label="Zoom out" size="sm" onClick={() => fgRef.current?.zoom(fgRef.current.zoom() / 1.4, reducedMotion ? 0 : 260)}>
          <Minus className="h-4 w-4" strokeWidth={1.75} />
        </IconButton>
        <IconButton label="Fit (F)" size="sm" onClick={() => flyToView(frameFit(pointsOf(), 48, 2.4))}>
          <Scan className="h-4 w-4" strokeWidth={1.75} />
        </IconButton>
        {!props.compactControls && (
          <>
            <span className="my-0.5 h-px w-5 bg-neutral-200" aria-hidden="true" />
            <IconButton
              label={showLabels ? 'Hide labels' : 'Show labels'}
              size="sm"
              onClick={props.onToggleLabels}
              aria-pressed={showLabels}
              className={showLabels ? '' : '!text-neutral-300'}
            >
              <Tag className="h-4 w-4" strokeWidth={1.75} />
            </IconButton>
            <IconButton label={paused ? 'Resume layout' : 'Pause layout'} size="sm" onClick={togglePaused} aria-pressed={paused}>
              {paused ? <Play className="h-4 w-4" strokeWidth={1.75} /> : <Pause className="h-4 w-4" strokeWidth={1.75} />}
            </IconButton>
            <IconButton label="Reset layout" size="sm" onClick={resetLayout}>
              <RotateCcw className="h-4 w-4" strokeWidth={1.75} />
            </IconButton>
          </>
        )}
      </div>
    </div>
  );
});
NetworkCanvas.displayName = 'NetworkCanvas';

